import React, { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setInView(true), io.disconnect())),
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

const reveal = (inView, delay = 0) => ({
  opacity: inView ? 1 : 0,
  transform: inView ? "translateY(0)" : "translateY(12px)",
  transition: `opacity 480ms ease ${delay}ms, transform 480ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
});

const body = "rgba(26,26,25,0.78)";

function Section({ title, children, testid }) {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref}
      className="border-t pt-12 sm:pt-14"
      style={{ borderColor: "rgba(26,26,25,0.10)" }}
      data-testid={testid}
    >
      <h2
        className="font-display tracking-[0.22em] uppercase"
        style={{
          fontSize: "clamp(0.95rem, 1.1vw, 1.05rem)",
          fontWeight: 500,
          color: "#0E0E0D",
          ...reveal(inView, 0),
        }}
      >
        {title}
      </h2>
      <ul
        className="mt-6 text-base sm:text-[16.5px] leading-[1.95] max-w-[640px] space-y-3"
        style={{ color: body, ...reveal(inView, 120) }}
      >
        {children}
      </ul>
    </section>
  );
}

export default function CareInstructions() {
  const [topRef, topInView] = useInView();

  return (
    <main
      ref={topRef}
      className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pt-20 sm:pt-28 pb-24 sm:pb-32"
      data-testid="care-instructions-page"
    >
      {/* Header */}
      <div className="max-w-[820px]">
        <h1
          className="font-display leading-[1.05]"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.25rem)",
            letterSpacing: "-0.005em",
            fontWeight: 400,
            ...reveal(topInView, 0),
          }}
          data-testid="care-heading"
        >
          Care <span className="italic">Instructions</span>
        </h1>
        <p
          className="mt-10 sm:mt-12 text-base sm:text-[17px] leading-[1.85]"
          style={{ color: body, ...reveal(topInView, 160), maxWidth: "600px" }}
          data-testid="care-intro"
        >
          Thoughtful care helps preserve the structure, appearance, and longevity of your Tamoré piece.
        </p>
      </div>

      {/* Sections */}
      <div className="mt-20 sm:mt-24 space-y-16 sm:space-y-20">
        <Section title="Care Recommendations" testid="care-recommendations">
          <li>· Dry clean recommended.</li>
          <li>· Store garments in a cool, dry place.</li>
          <li>· Avoid prolonged exposure to direct sunlight.</li>
          <li>· Steam gently when required.</li>
          <li>· Avoid contact with rough surfaces or sharp accessories.</li>
        </Section>

        <Section title="Washing" testid="care-washing">
          <li>· Do not machine wash.</li>
          <li>· Do not bleach.</li>
          <li>· Do not tumble dry.</li>
          <li>· Do not wring or twist the garment.</li>
        </Section>

        <Section title="Ironing & Steaming" testid="care-ironing">
          <li>· Steam lightly to remove creases.</li>
          <li>· If ironing is required, use a low temperature setting.</li>
          <li>· Always iron on the reverse side or with a pressing cloth.</li>
        </Section>

        <Section title="Storage" testid="care-storage">
          <li>· Hang garments carefully to maintain their shape.</li>
          <li>· Avoid overcrowding wardrobes.</li>
          <li>· Ensure garments are clean before long-term storage.</li>
        </Section>
      </div>
    </main>
  );
}
